import React, { PropsWithChildren, ReactNode, useCallback, useEffect, useState } from 'react'
import Heading from 'LuxKit/Typography/Heading'
import { useInView } from 'react-intersection-observer'
import useMountedEffect from 'hooks/useMountedEffect'
import noop from 'lib/function/noop'
import debounce from 'lodash.debounce'
import LargeContentToggle from 'components/Common/LargeContentToggle'

interface Props {
  heading?: string | ReactNode;
  id?: string;
  className?: string;
  onInViewChange?: (id: string, inView: boolean) => void;
  initialHeight: number;
  minimumContentHeight?: number;
  open?: boolean;
  onToggle?: (id: string) => void;
}

function NavigableOfferContentGroup(props: PropsWithChildren<Props>) {
  const {
    children,
    heading,
    id,
    onInViewChange = noop,
    initialHeight,
    minimumContentHeight,
    open,
    onToggle = noop,
  } = props

  const [bottomOffset, setBottomOffset] = useState(0)

  const [viewRef, inView] = useInView({ rootMargin: `-60px 0px ${bottomOffset}px 0px` })

  useEffect(() => {
    const onResize = debounce(() => {
      // size of the header bar (60) + size of margin between items (20)
      const bottomOffset = 80 - window.innerHeight
      setBottomOffset(bottomOffset)
    }, 1000)

    window.addEventListener('resize', onResize)
    onResize()

    return () => {
      window.removeEventListener('resize', onResize)
    }
  }, [])

  useMountedEffect(() => {
    onInViewChange(id, inView)
  }, [inView])

  const onViewMoreChange = useCallback(() => {
    onToggle(id)
  }, [id, onToggle])

  return (
    <div id={id} ref={viewRef}>
      <Heading variant="heading3">{heading}</Heading>
      <br />
      <LargeContentToggle
        open={open}
        maxHeight={initialHeight}
        showLessText="Read less"
        showMoreText="Read more"
        onToggle={onViewMoreChange}
        minimumContentHeight={minimumContentHeight}
      >
        {children}
      </LargeContentToggle>
    </div>
  )
}

NavigableOfferContentGroup.displayName = 'NavigableOfferContentGroup'

export default NavigableOfferContentGroup
